/**
 * Shared constants for the matches feature, hoisted out of `features/matches/`
 * so cross-feature consumers (e.g. `features/admin/`) and the central
 * `hooks/api.ts` can import them without violating feature isolation.
 */

/**
 * Score threshold above which a match is considered a "strong fit" tier 1 match.
 * Used by both the investors page (for tiering) and recommended-investor-card
 * (for tier-color selection). Keep in sync with `tierFor()` thresholds.
 */
export const STRONG_FIT_THRESHOLD = 70

/** Minimum number of matches always shown in the "Strongest fit" tier so the page never feels empty. */
export const MIN_STRONG_COUNT = 3

/**
 * Typical number of reachable GPs/Principals per fund. Used to estimate the
 * "Contacts to reach" stat before activity research has populated real contacts
 * for every matched fund. Deliberately based on the displayed match count so
 * the estimate always stays consistent with the "Investors matched" stat.
 * Calibrated against observed contact density once research completes (~85
 * contacts across ~27 matched funds).
 */
export const ESTIMATED_CONTACTS_PER_FUND = 3.15

/**
 * Resolve the "Contacts to reach" stat. The backend reports `known` (real
 * contacts found so far) and `isComplete` (activity research has run for every
 * matched fund). Until research is complete we show a per-fund estimate keyed
 * off the displayed `matchesCount`, floored at the contacts we already know
 * about so it never reads lower than reality.
 */
export function deriveContactsToReach(
  matchesCount: number,
  contactReach: { known: number; researchedCount?: number; isComplete: boolean } | null | undefined,
  fallback: number
): { count: number; estimated: boolean } {
  if (!contactReach) return { count: fallback, estimated: false }
  if (contactReach.isComplete) return { count: contactReach.known, estimated: false }
  const unresearched = matchesCount - (contactReach.researchedCount ?? 0)
  return {
    count: contactReach.known + Math.ceil(ESTIMATED_CONTACTS_PER_FUND * unresearched),
    estimated: true,
  }
}

/** Polling interval (ms) used while a matcher refresh is in flight. */
export const MATCHER_REFRESH_POLL_MS = 5_000

/** Typical wall-clock duration of a per-investor evaluation, used for progress UI. */
export const TYPICAL_EVALUATION_DURATION_S = 450

/** Typical wall-clock duration of a pre-screen evaluation (deck-only, faster). */
export const TYPICAL_PRESCREEN_DURATION_S = 180

/**
 * Typical wall-clock duration of a per-investor activity-research run
 * (LinkedIn scrape + Google search + LLM enrichment of contacts/portfolio).
 * Used to show an approximate ETA while contacts are being prepared.
 */
export const TYPICAL_ACTIVITY_RESEARCH_DURATION_S = 120

/**
 * Concurrency of the engine's activity-research stream consumer. Used to
 * estimate wall-clock for a fan-out of N investors:
 *   eta_seconds ≈ ceil(N / ACTIVITY_RESEARCH_CONCURRENCY) * TYPICAL_ACTIVITY_RESEARCH_DURATION_S
 * Keep in sync with `apps/engine/src/engine/queues/registry.py` (max_concurrent
 * for stream:investor-activity-research).
 */
export const ACTIVITY_RESEARCH_CONCURRENCY = 2

/** Price of a single Investor Lens evaluation. */
export const EVALUATION_COST_CREDITS = 1
export const EVALUATION_COST_USD_DISPLAY = '~$2'

/**
 * Status values for InvestorEvaluationRequest — mirrors the backend Prisma enum.
 * Compared in recommended-investor-card.tsx and hooks/api.ts polling logic.
 */
export const EVALUATION_REQUEST_STATUS = {
  PENDING_PERSONA: 'PENDING_PERSONA',
  PENDING_EVALUATION: 'PENDING_EVALUATION',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const
export type EvaluationRequestStatus =
  (typeof EVALUATION_REQUEST_STATUS)[keyof typeof EVALUATION_REQUEST_STATUS]

/**
 * Kind of investor evaluation request — mirrors the backend Prisma enum
 * `InvestorEvaluationRequestKind`. Drives the pre-screen UX (badge, callout,
 * banner) on the matches list and evaluation result page.
 */
export const EVALUATION_REQUEST_KIND = {
  DEEP_EVALUATION: 'DEEP_EVALUATION',
  PRESCREEN: 'PRESCREEN',
} as const
export type EvaluationRequestKind =
  (typeof EVALUATION_REQUEST_KIND)[keyof typeof EVALUATION_REQUEST_KIND]
